import { h, Host, writeTask } from "@stencil/core";
import { get } from "../../service/ajax-service";
import { debounce, DEBOUNCE_DELAY } from "../../service/debounce";
import { AppState } from "../../service/app-state";
import { translate } from "../../service/translation-service";
const NO_SUGGESTION_SELECTED = -1;
const MIN_LENGTH = 3;
function normalizeUrl(url) {
    const urlObject = document.createElement('a');
    urlObject.href = url.replace('%20', '+');
    return urlObject.href;
}
export class SearchBox {
    constructor() {
        this.open = false;
        this.isClosing = false;
        this.query = '';
        this.suggestions = [];
        this.suggestionsCursor = NO_SUGGESTION_SELECTED;
        this.isHeaderSearchbox = false;
        this.fetchSuggestions = debounce(async (term) => {
            const url = new URL(normalizeUrl(this.suggestUrl));
            url.searchParams.append(this.suggestParameter, term);
            try {
                return await get(url.toString());
            }
            catch (e) {
                return {
                    status: false,
                    suggestion: term,
                    suggestions: {}
                };
            }
        }, DEBOUNCE_DELAY);
    }
    componentWillLoad() {
        this.isHeaderSearchbox = this.el.closest('.header') !== null;
        this.onPopstate();
    }
    onAppStateChange(e) {
        this.open = e.detail.search;
        if (this.open && this.isHeaderSearchbox) {
            this.el.querySelector('input[type="search"]').focus();
        }
    }
    onReset() {
        this.query = '';
        this.suggestions = [];
        this.suggestionsCursor = NO_SUGGESTION_SELECTED;
    }
    onSubmit(e) {
        e.preventDefault();
        if (this.suggestions[this.suggestionsCursor]) {
            this.query = this.suggestions[this.suggestionsCursor].name;
        }
        else {
        }
        this.suggestions = [];
        this.submitSuggestion();
    }
    async onQueryChanged() {
        if (this.query.length <= MIN_LENGTH) {
            this.suggestionsCursor = NO_SUGGESTION_SELECTED;
            this.suggestions = [];
            return;
        }
        const { suggestions } = await this.fetchSuggestions(this.query);
        this.suggestions = Object.keys(suggestions || []).map(key => ({ name: key, count: suggestions[key] }));
        this.suggestionsCursor = NO_SUGGESTION_SELECTED;
    }
    onPopstate() {
        // Do not sync search query if search box is in header, as it's not possible to close it
        if (this.isHeaderSearchbox) {
            return;
        }
        const { searchParams } = new URL(window.location.href);
        this.query = searchParams.has(this.searchParameter) ? searchParams.get(this.searchParameter) : '';
    }
    onInput(event) {
        this.query = event.target.value;
    }
    onBlur() {
        this.suggestions = [];
    }
    onKeyDown(event) {
        if (!this.suggestions.length) {
            return;
        }
        switch (event.key.toLocaleLowerCase()) {
            case 'arrowup':
            case 'up':
                if (this.suggestionsCursor <= NO_SUGGESTION_SELECTED) {
                    this.suggestionsCursor = this.suggestions.length - 1;
                }
                else {
                    this.suggestionsCursor = Math.max(0, this.suggestionsCursor - 1);
                }
                event.preventDefault();
                break;
            case 'arrowdown':
            case 'down':
                if (this.suggestionsCursor <= NO_SUGGESTION_SELECTED) {
                    this.suggestionsCursor = 0;
                }
                else {
                    this.suggestionsCursor = Math.min(this.suggestions.length - 1, this.suggestionsCursor + 1);
                }
                event.preventDefault();
                break;
        }
    }
    onSuggestMouseDown(term) {
        writeTask(() => {
            this.query = term;
            this.submitSuggestion();
        });
    }
    onSuggestMouseEnter(suggestion) {
        this.suggestionsCursor = this.suggestions.indexOf(suggestion);
    }
    renderSuggest() {
        if (!this.suggestions.length) {
            return;
        }
        const suggestions = this.suggestions.map((suggestion, index) => {
            const { name } = suggestion;
            const label = name.replace(new RegExp(this.query, 'gi'), `<strong>${this.query}</strong>`);
            return h("li", { class: { 'active': this.suggestionsCursor === index }, onMouseDown: _ => this.onSuggestMouseDown(suggestion.name), onMouseEnter: _ => this.onSuggestMouseEnter(suggestion), innerHTML: label });
        });
        return h("ul", { class: "list-unstyled" }, suggestions);
    }
    submitSuggestion() {
        if (this.query.length === 0) {
            this.close();
            return;
        }
        const currentUrl = new URL(window.location.href);
        const resultUrl = new URL(normalizeUrl(this.searchUrl));
        if (resultUrl.pathname === currentUrl.pathname) {
            resultUrl.searchParams.set(this.searchParameter, this.query);
            window.history.replaceState({}, '', resultUrl.toString());
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
        else {
            resultUrl.searchParams.set(this.searchParameter, this.query);
            window.location.href = resultUrl.toString();
        }
        if (this.isHeaderSearchbox) {
            this.close();
        }
    }
    close() {
        this.query = '';
        AppState.toggleSearch();
        this.isClosing = true;
        setTimeout(() => {
            this.isClosing = false;
        }, 400);
    }
    render() {
        return (h(Host, { key: '3abb2482f244045ae266126d293b5580fbeafdb2', class: {
                'searchbox': true,
                'active': this.open,
                'is-closing': this.isClosing
            } }, h("form", { key: 'a0b5fe523e572176c6859d6e2f6268ca26103957', name: "search", novalidate: "novalidate" }, h("input", { key: '19f4ce2de37f21c56ed4e7444c0da8c6c380d822', type: "search", name: "search", value: this.query, onChange: (e) => this.query = e.target.value, onInput: e => this.onInput(e), onFocus: _ => this.onQueryChanged(), onBlur: _ => this.onBlur(), onKeyDown: e => this.onKeyDown(e), autocomplete: "off", placeholder: translate('searchbox.placeholder'), "aria-label": translate('searchbox.placeholder') }), this.renderSuggest(), h("button", { key: 'd4cde878d13b4a6adc8957a7f3eac62f830a2b4c', type: "submit", class: "btn-icon", title: "Submit" }, h("svg", { key: '83544ea347f8c50ab4627edcf024b0cce8876d6b', xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 512 512", width: "24", height: "25" }, h("use", { key: '0dc030c9467c70bff6a0161c08bff0952931e2de', href: "#search" }))))));
    }
    static get is() { return "uvex-search-box"; }
    static get originalStyleUrls() {
        return {
            "$": ["./search-box.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["search-box.css"]
        };
    }
    static get properties() {
        return {
            "searchUrl": {
                "type": "string",
                "attribute": "search-url",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "searchParameter": {
                "type": "string",
                "attribute": "search-parameter",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "suggestUrl": {
                "type": "string",
                "attribute": "suggest-url",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "suggestParameter": {
                "type": "string",
                "attribute": "suggest-parameter",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "open": {},
            "isClosing": {},
            "query": {},
            "suggestions": {},
            "suggestionsCursor": {}
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "query",
                "methodName": "onQueryChanged"
            }];
    }
    static get listeners() {
        return [{
                "name": "uvex-app-state-change",
                "method": "onAppStateChange",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "reset",
                "method": "onReset",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "submit",
                "method": "onSubmit",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "popstate",
                "method": "onPopstate",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=search-box.js.map
